<template>
  <section>
    <div class="popups">
      <b-sidebar
        v-model="showFilters"
        right
        shadow
        backdrop
        bg-variant="white"
        no-header
      >
        <template #default="{ hide }">
          <div class="container sidebar-header px-2">
            <b-row>
              <div class="col">
                <b-button
                  variant="primary"
                  @click="applyCustomFilters"
                  >Apply</b-button
                >
                <b-button
                  variant="outline-secondary"
                  class="ml-2"
                  @click="resetCustomFilters"
                  >Reset</b-button
                >
              </div>
              <div class="col-auto text-right">
                <b-button variant="link" class="ml-3 px-0" @click="hide">
                  <i class="fa fa-times"></i>
                </b-button>
              </div>
            </b-row>
          </div>

          <div class="px-3 py-2">
            <b-form-group
              label="Maintain Weeks of Stock (Source):"
              description="Number of weeks of stock to maintain at sending warehouse"
            >
              <b-form-input
                v-model.number="formFilters.weeksOfStockOutgoing"
                type="number"
                min="1"
              />
            </b-form-group>

            <b-form-group
              label="Ensure Weeks of Stock (Destination):"
              description="Number of weeks of stock to ensure at receiving warehouse. Note that this does not include weeks in transit for the WT, if you know the estimated time of weeks the WT will take, you should add it here"
            >
              <b-form-input
                v-model.number="formFilters.weeksOfStockIncoming"
                type="number"
                min="1"
              />
            </b-form-group>

            <b-form-group
              label="Minimum Quantity to Send:"
              description="Minimum quantity required to justify a transfer"
            >
              <b-form-input
                v-model.number="formFilters.minQuantityToSend"
                type="number"
                min="1"
              />
            </b-form-group>

            <b-form-group
              label="Currently Has X Weeks of Stock Left:"
              description="How many weeks the current inventory should still last at the destination (allows you to filter out which items you want to transfer inventory for). Example: Set to 3 to only show 'urgent' items."
            >
              <b-form-input
                v-model.number="formFilters.hasStockLessThenWeeks"
                type="number"
                min="1"
              />
            </b-form-group>
            <b-form-group
              label="Item SKU:"
            >
              <b-form-input
                v-model.number="formFilters.sku"
                type="string"
              />
            </b-form-group>

            <b-form-group
              label="Use Storefront Sales Average:"
              description="When enabled, uses storefront sales average for calculations"
            >
              <b-form-checkbox
                v-model="formFilters.useStorefrontSalesAverage"
                switch
              >
                {{ formFilters.useStorefrontSalesAverage ? 'Yes' : 'No' }}
              </b-form-checkbox>
            </b-form-group>

            <b-form-group
              label="From Warehouses (Source):"
              description="Warehouses with excess stock (potential senders)"
            >
              <b-v-select-multi
                v-model="formFilters.targetFromWarehouses"
                :searchable="true"
                :multiple="true"
                :label-title="'All'"
                :options="warehousesFilter"
              />
            </b-form-group>

            <b-form-group
              label="To Warehouses (Destination):"
              description="Warehouses needing inventory (potential receivers)"
            >
              <b-v-select-multi
                v-model="formFilters.targetWarehouses"
                :searchable="true"
                :multiple="true"
                :label-title="'All'"
                :options="warehousesFilter"
              />
            </b-form-group>
          </div>
        </template>
      </b-sidebar>
    </div>

    <div class="header">
      <div class="header-body">
        <b-row class="align-items-end">
          <div class="col">
            <h1 class="header-title">
              Recommended Warehouse Product Transfers
            </h1>
            <p class="text-muted">
              Analyze inventory levels and get recommendations for product transfers between warehouses. Note that we only use storefront sales for the AVG on this page.
            </p>
          </div>
          <div class="col-auto">
            <b-button
              variant="success"
              class="mr-2"
              :disabled="selectedItems.length === 0"
              @click="createMultipleWarehouseTransfers"
            >
              <i class="fa fa-exchange-alt mr-2"></i>
              Transfer Selected ({{ selectedItems.length }})
            </b-button>
            <b-button variant="primary" @click="showFilters = !showFilters">
              <i class="fa fa-filter mr-2"></i>
              Filters
            </b-button>
          </div>
        </b-row>
      </div>
    </div>

    <TableListing
      class="mb-0"
      :loading-page="loadingItems"
      :failed-loading="failedLoadItems"
      head-variant="dark"
      :items.sync="items"
      :fields.sync="fields"
      :pagination.sync="pagination"
      :options.sync="tableOptions"
      v-on:pagination-changed="loadItems"
    >
      <template v-slot:cell(selected)="data">
        <b-form-checkbox
          :checked="isItemSelected(data.item)"
          @change="toggleItemSelection(data.item)"
        />
      </template>


      <template v-slot:cell(fromWarehouseId)="data">
        <span
          v-if="myWarehouses && myWarehouses.find(a => a.id == data.item.fromWarehouseId)"
          >{{ myWarehouses.find(a => a.id == data.item.fromWarehouseId).name }}</span
        >
        <span v-else>N / A</span>
      </template>

      <template v-slot:cell(toWarehouseId)="data">
        <span
          v-if="myWarehouses && myWarehouses.find(a => a.id == data.item.toWarehouseId)"
          >{{ myWarehouses.find(a => a.id == data.item.toWarehouseId).name }}</span
        >
        <span v-else>N / A</span>
        <div class="pt-1">
          <span class="badge" :class="getInventoryStatusClass(data.item.inventoryStatus)">
            {{ (data.item.inventoryStatus) }}
          </span>
        </div>
      </template>


      <template v-slot:cell(currentQtySource)="data">
        <span class="badge badge-info">{{ data.item.currentQtySource }}</span>
        <small class="text-muted d-block">(AVG: {{ data.item.perWeekAvgSource | roundDecimal(2) }} / week)</small>
      </template>

      <template v-slot:cell(quantityCanSend)="data">
        <span class="badge badge-primary">{{ data.item.quantityCanSend | roundDecimal(0) }}</span>
      </template>
      <template v-slot:cell(item)="data">
          <div>
            <CopyText :text="data.item.item" />
          </div>
          <div class="border-top">
            <span class="text-secondary">{{ data.item.description }}</span>
          </div>
      </template>

      <template v-slot:cell(brandId)="data">
        <span
          v-if="myBrands && myBrands.find(a => a.id == data.item.brandId)"
          >{{ myBrands.find(a => a.id == data.item.brandId).name }}</span
        >
        <span v-else>N / A</span>
      </template>
      <template v-slot:cell(currentQtyDestination)="data">
        <span class="badge badge-warning">{{ data.item.currentQtyDestination }}</span>
        <small class="text-muted d-block">(AVG: {{ data.item.perWeekAvgDestination | roundDecimal(2) }} / week)</small>
      </template>

      <template v-slot:cell(recommendedTransferQuantity)="data">
        <span class="badge badge-success">{{ Math.floor(data.item.recommendedTransferQuantity) }}</span>
      </template>

      <template v-slot:cell(action)="data">
        <b-button
          variant="success"
          class="mr-2"
          @click="createWarehouseTransfer(data.item)"
        >
          <i class="fa fa-plus mr-2"></i>
          Create WT
        </b-button>
      </template>
    </TableListing>
  </section>
</template>

<script>
import TableListing from '@/views/components/TableListing.vue';
import { myCommonDataMixin } from '@/helpers/utils/vueMixins.js';

export default {
  name: 'Warehouses.RecommendedItemsToTransfer',
  mixins: [myCommonDataMixin],
  components: {
    TableListing
  },

  data() {
    return {
      showFilters: false,
      loadingItems: false,
      failedLoadItems: false,
      items: [],

      pagination: {
        page: 1,
        perPage: 25,
        totalRows: 0
      },

      tableOptions: {
        sortBy: '',
        sortDesc: false
      },

      filters: {
        weeksOfStockOutgoing: 8,
        weeksOfStockIncoming: 12,
        minQuantityToSend: 100,
        sku: '',
        hasStockLessThenWeeks: 12,
        useStorefrontSalesAverage: false,
        targetFromWarehouses: [],
        targetWarehouses: [],
        targetFromWarehouseId: '',
        targetWarehouseId: ''
      },

      formFilters: {
        weeksOfStockOutgoing: 8,
        weeksOfStockIncoming: 12,
        minQuantityToSend: 100,
        sku: '',
        hasStockLessThenWeeks: 12,
        useStorefrontSalesAverage: false,
        targetFromWarehouses: [],
        targetWarehouses: [],
        targetFromWarehouseId: '',
        targetWarehouseId: ''
      },

      defaultFilters: {
        weeksOfStockOutgoing: 8,
        weeksOfStockIncoming: 12,
        minQuantityToSend: 100,
        sku: '',
        hasStockLessThenWeeks: 12,
        useStorefrontSalesAverage: false,
        targetFromWarehouses: [],
        targetWarehouses: [],
        targetFromWarehouseId: '',
        targetWarehouseId: ''
      },

      selectedItems: [],

      fields: [
        { key: 'selected', label: '', sortable: false },
        { key: 'brandId', label: 'Brand', sortable: false },
        { key: 'fromWarehouseId', label: 'From Warehouse', sortable: false },
        { key: 'item', label: 'Item SKU', sortable: false },
        { key: 'currentQtySource', label: 'Source Qty', sortable: false },
        { key: 'quantityCanSend', label: 'Can Send', sortable: false },
        { key: 'toWarehouseId', label: 'To Warehouse', sortable: false },
        { key: 'currentQtyDestination', label: 'Destination Qty', sortable: false },
        { key: 'recommendedTransferQuantity', label: 'Recommended Qty', sortable: false },
        { key: 'action', label: 'Actions', sortable: false }
      ]
    };
  },

  mounted() {
    this.loadItems();
  },

  methods: {
    applyCustomFilters() {
      this.filters.sku = this.formFilters.sku;
      this.filters.weeksOfStockOutgoing = this.formFilters.weeksOfStockOutgoing;
      this.filters.weeksOfStockIncoming = this.formFilters.weeksOfStockIncoming;
      this.filters.minQuantityToSend = this.formFilters.minQuantityToSend;
      this.filters.hasStockLessThenWeeks = this.formFilters.hasStockLessThenWeeks;
      this.filters.useStorefrontSalesAverage = this.formFilters.useStorefrontSalesAverage;
      this.filters.targetFromWarehouses = this.formFilters.targetFromWarehouses;
      this.filters.targetWarehouses = this.formFilters.targetWarehouses;

      this.pagination.page = 1;
      this.loadItems();
      this.showFilters = false;
    },

    resetCustomFilters() {
      this.formFilters.weeksOfStockOutgoing = this.defaultFilters.weeksOfStockOutgoing;
      this.formFilters.weeksOfStockIncoming = this.defaultFilters.weeksOfStockIncoming;
      this.formFilters.minQuantityToSend = this.defaultFilters.minQuantityToSend;
      this.formFilters.hasStockLessThenWeeks = this.defaultFilters.hasStockLessThenWeeks;
      this.formFilters.useStorefrontSalesAverage = this.defaultFilters.useStorefrontSalesAverage;
      this.formFilters.targetFromWarehouses = [];
      this.formFilters.targetWarehouses = [];
    },

    getInventoryStatusClass(status) {
      switch (status) {
        case 'Normal': return 'badge-secondary';
        case 'Warning': return 'badge-warning';
        case 'Urgent': return 'badge-danger';
        default: return 'badge-secondary';
      }
    },

    loadItems() {
      this.loadingItems = true;
      this.failedLoadItems = false;

      const payload = {
        page: this.pagination.page,
        perPage: this.pagination.perPage,
        sku: this.filters.sku,
        weeksOfStockSource: this.filters.weeksOfStockOutgoing,
        weeksOfStockDestination: this.filters.weeksOfStockIncoming,
        minQuantityToSend: this.filters.minQuantityToSend,
        hasStockLessThenWeeks: this.filters.hasStockLessThenWeeks,
        useStorefrontSalesAverage: this.filters.useStorefrontSalesAverage,
        targetFromWarehouses: this.filters.targetFromWarehouses,
        targetWarehouses: this.filters.targetWarehouses
      };

      this.$http
        .post('/api/common/warehouse-shipments/recommended-transfers', payload)
        .then(resp => {
          if (resp.data) {
            this.items = resp.data.items || [];
            this.pagination.totalRows = resp.data.total;
            this.tableOptions.totalRows = resp.data.total;
            this.pagination.page = resp.data.page;
            this.pagination.perPage = resp.data.perPage;
          } else {
            this.items = [];
            this.pagination.totalRows = 0;
          }
        })
        .catch(err => {
          console.error('Error loading recommended transfers:', err);
          this.failedLoadItems = true;
          this.$swal({
            title: 'Error!',
            text: (err.response && err.response.data && err.response.data.errorMessage) || 'Failed to load recommendations',
            icon: 'error'
          });
        })
        .finally(() => {
          this.loadingItems = false;
        });
    },


    isItemSelected(item) {
      return this.selectedItems.some(function(selected) {
        return selected.item === item.item &&
               selected.brandId === item.brandId &&
               selected.fromWarehouseId === item.fromWarehouseId &&
               selected.toWarehouseId === item.toWarehouseId;
      });
    },

    toggleItemSelection(item) {
      var index = this.selectedItems.findIndex(function(selected) {
        return selected.item === item.item &&
               selected.brandId === item.brandId &&
               selected.fromWarehouseId === item.fromWarehouseId &&
               selected.toWarehouseId === item.toWarehouseId;
      });
      if (index > -1) {
        this.selectedItems.splice(index, 1);
      } else {
        this.selectedItems.push(item);
      }
    },

    createWarehouseTransfer(item) {
      var recommendedWtForm = {
        brandId: item.brandId,
        fromWarehouseId: item.fromWarehouseId,
        toWarehouseId: item.toWarehouseId,
        items: [
          {
            sku: item.item,
            quantity: Math.floor(item.recommendedTransferQuantity)
          }
        ]
      };

      this.$store.commit('warehouseTransfers/setRecommendedForm', recommendedWtForm);

      this.$router.push({
        name: 'Warehouses.WarehouseTransfers.Create',
        query: { recommendedWt: true }
      });
    },

    createMultipleWarehouseTransfers() {
      if (this.selectedItems.length === 0) {
        return;
      }

      // Check if all selected items have the same brandId, fromWarehouseId, and toWarehouseId
      var firstItem = this.selectedItems[0];
      var allSame = this.selectedItems.every(function(item) {
        return item.brandId === firstItem.brandId &&
               item.fromWarehouseId === firstItem.fromWarehouseId &&
               item.toWarehouseId === firstItem.toWarehouseId;
      });

      if (!allSame) {
        this.$swal({
          title: 'Cannot Create Transfer',
          text: 'All selected items must have the same Brand, From Warehouse, and To Warehouse to create a single transfer.',
          icon: 'warning'
        });
        return;
      }

      var items = this.selectedItems.map(function(item) {
        return {
          sku: item.item,
          quantity: Math.floor(item.recommendedTransferQuantity)
        };
      });

      var recommendedWtForm = {
        brandId: firstItem.brandId,
        fromWarehouseId: firstItem.fromWarehouseId,
        toWarehouseId: firstItem.toWarehouseId,
        items: items
      };
      this.$store.commit('warehouseTransfers/setRecommendedForm', recommendedWtForm);

      this.$router.push({
        name: 'Warehouses.WarehouseTransfers.Create',
        query: { recommendedWt: true }
      });
    }
  }
};
</script>

<style scoped>
.sidebar-header {
  padding: 1rem 0;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 1rem;
}
</style>
